import { useEffect, useRef, useState } from "react";

import { Grid, Typography, Box } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Big from "big.js";

import CurrencySelectionView from "./CurrencySelectionView";
import { WHITE_FFF } from "../styles/Colors";
import useFxRates from "../hooks/UseFxRates";

import { getCcyDataByCcy } from "../utils/CurrencyHelper";
import {
	formatRateNumber,
	fromPlainToDisplayValueWithRounding,
	getNumber,
	ROUND,
} from "../utils/NumberHelper";
import LockIcon from "@mui/icons-material/Lock";
import CurrencyConfig from "../json/CurrencyConfig.json";
import { Objects } from "../models/CurrencyPair";
import { FOCUS_SIDE_LEFT, FOCUS_SIDE_RIGHT } from "../utils/Constants";
import { priceStore } from "../zustand/priceStore";

const boxStyle = {
	direction: "column",
	alignItems: "center",
	justifyContent: "space-between",
};
interface Price {
	ccyPair: string;
	midRate: number;
}

function ConversionView() {
	const [leftAmount, setLeftAmount] = useState<string>("");
	const [rightAmount, setRightAmount] = useState<string>("");
	const [leftCcy, setLeftCcy] = useState<string>("");
	const [rightCcy, setRightCcy] = useState<string>("");
	const [leftCcyName, setLeftCcyName] = useState<string>("");
	const [rightCcyName, setRightCcyName] = useState<string>("");
	const [focusedSide, setFocusedSide] = useState<string>("");
	// const [focusedSideCcy, setFocusedSideCcy] = useState<string>("");
	const [leftCcyRate, setLeftCcyRate] = useState<string>("");
	const [rightCcyRate, setRightCcyRate] = useState<string>("");
	const lastLeftAmount = useRef<string>("");
	const lastRightAmount = useRef<string>("");
	const lastLeftCcy = useRef("");
	const lastRightCcy = useRef("");
	const currencyConfig: Objects = CurrencyConfig;

	const theme = createTheme();
	const ccyChange = priceStore.subscribe((state) => state.prices);
	const prices = priceStore((state) => state.prices);

	theme.typography.body1 = {
		fontSize: "20px",
		color: WHITE_FFF,
		fontFamily: "Avenir",
		[theme.breakpoints.down("sm")]: {
			fontSize: "12px",
		},
	};

	const {
		thousandsSeparator: leftCcySep,
		decimalDigits: leftDecimalDigits,
		decimalSeparator: leftDecimalPoint,
	} = getCcyDataByCcy(leftCcy);
	const {
		thousandsSeparator: rightCcySep,
		decimalDigits: rightDecimalDigits,
		decimalSeparator: rightDecimalPoint,
	} = getCcyDataByCcy(rightCcy);

	const onChangeLeftCcy = (event: any, data: any) => {
		typeof data === "string" ? setLeftCcy(data) : setLeftCcy(data.props.value);
		//setFocusedSideCcy(data.props.value);
	};

	const onChangeRightCcy = (event: any, data: any) => {
		typeof data === "string"
			? setRightCcy(data)
			: setRightCcy(data.props.value);
		//setFocusedSideCcy(data.props.value);
	};

	const { calculateFxRate } = useFxRates({ ccy1: leftCcy, ccy2: rightCcy });

	//calcuate initial rates
	useEffect(() => {
		const usdSgdPrice: Price | undefined = prices.find(
			(price) => price.ccyPair === "USD/SGD"
		);
		const usdSgdMidRate: number | undefined = usdSgdPrice?.midRate;
		if (usdSgdMidRate && leftCcy === "" && rightCcy === "") {
			setRightCcy("USD");
			setLeftCcy("SGD");
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ccyChange]);

	// calculate rates when currency changes
	useEffect(() => {
		if (leftCcy !== "" && rightCcy !== "") {
			setLeftCcyName(currencyConfig[leftCcy].name.en_name);
			setRightCcyName(currencyConfig[rightCcy].name.en_name);

			let leftCurrencyRate = formatRateNumber(
				calculateFxRate(leftCcy, rightCcy),
				rightCcy
			);
			setLeftCcyRate(leftCurrencyRate);

			let rightCurrencyRate = formatRateNumber(
				1 / calculateFxRate(leftCcy, rightCcy),
				leftCcy
			);
			setRightCcyRate(rightCurrencyRate);
		}

		return () => ccyChange();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leftCcy, rightCcy, ccyChange]);

	//update right amount when change left amount
	useEffect(() => {
		if (
			(focusedSide === FOCUS_SIDE_LEFT &&
				lastLeftAmount.current !== leftAmount &&
				lastRightCcy.current === rightCcy) ||
			(lastLeftCcy.current !== leftCcy &&
				lastRightCcy.current === rightCcy &&
				focusedSide === FOCUS_SIDE_LEFT) ||
			(focusedSide === FOCUS_SIDE_LEFT &&
				lastLeftAmount.current === leftAmount &&
				lastRightCcy.current === rightCcy)
		) {
			if (leftCcy !== "" && rightCcy !== "") {
				lastLeftAmount.current = leftAmount;
				lastLeftCcy.current = leftCcy;
				lastRightCcy.current = rightCcy;
				const leftAmt = getNumber(
					leftAmount,
					leftCcySep,
					leftDecimalPoint,
					leftDecimalDigits
				);

				if (leftAmt > 0) {
					const newRightAmt = Number(
						Big(leftAmt).times(calculateFxRate(leftCcy, rightCcy))
					);
					lastRightAmount.current = fromPlainToDisplayValueWithRounding(
						newRightAmt,
						rightCcySep,
						rightDecimalPoint,
						rightDecimalDigits,
						ROUND.HALF_UP
					);
					setRightAmount(lastRightAmount.current);
				}
			}
		} else if (
			focusedSide === FOCUS_SIDE_LEFT &&
			lastRightCcy.current !== rightCcy
		) {
			if (leftCcy !== "" && rightCcy !== "") {
				lastLeftAmount.current = leftAmount;
				lastLeftCcy.current = leftCcy;
				lastRightCcy.current = rightCcy;
				const leftAmt = getNumber(
					leftAmount,
					leftCcySep,
					leftDecimalPoint,
					leftDecimalDigits
				);

				if (leftAmt > 0) {
					const newRightAmt = Number(
						Big(leftAmt).times(calculateFxRate(leftCcy, rightCcy))
					);
					lastRightAmount.current = fromPlainToDisplayValueWithRounding(
						newRightAmt,
						rightCcySep,
						rightDecimalPoint,
						rightDecimalDigits,
						ROUND.HALF_UP
					);
					setRightAmount(lastRightAmount.current);
				}
			}
		} else if (focusedSide === "" && rightAmount !== "" && leftAmount === "") {
			if (leftCcy !== "" && rightCcy !== "") {
				const rightAmt = getNumber(
					rightAmount,
					rightCcySep,
					rightDecimalPoint,
					rightDecimalDigits
				);

				if (rightAmt > 0) {
					const newLeftAmt = Number(
						Big(rightAmt).times(calculateFxRate(rightCcy, leftCcy))
					);
					lastRightAmount.current = fromPlainToDisplayValueWithRounding(
						newLeftAmt,
						leftCcySep,
						leftDecimalPoint,
						leftDecimalDigits,
						ROUND.HALF_UP
					);
					setLeftAmount(lastRightAmount.current);
				}
			}
		}

		const timer = setTimeout(() => {
			fireGA4Event();
		}, 5000);

		return () => clearTimeout(timer);
	}, [
		focusedSide,
		leftCcy,
		leftAmount,
		leftCcySep,
		leftDecimalPoint,
		leftDecimalDigits,
		rightCcySep,
		rightDecimalPoint,
		rightDecimalDigits,
		calculateFxRate,
		rightCcy,
		rightAmount,
	]);

	//update left amount when change the right amount
	useEffect(() => {
		if (
			(focusedSide === FOCUS_SIDE_RIGHT &&
				lastRightAmount.current !== rightAmount &&
				lastLeftCcy.current === leftCcy) ||
			(lastRightCcy.current !== rightCcy &&
				lastLeftCcy.current === leftCcy &&
				focusedSide === FOCUS_SIDE_RIGHT) ||
			(focusedSide === FOCUS_SIDE_RIGHT &&
				lastRightAmount.current === rightAmount &&
				lastLeftCcy.current === leftCcy)
		) {
			if (leftCcy !== "" && rightCcy !== "") {
				lastLeftAmount.current = rightAmount;
				lastLeftCcy.current = leftCcy;
				lastRightCcy.current = rightCcy;
				const rightAmt = getNumber(
					rightAmount,
					rightCcySep,
					rightDecimalPoint,
					rightDecimalDigits
				);
				if (rightAmt > 0) {
					const newLeftAmt = Number(
						Big(rightAmt).times(calculateFxRate(rightCcy, leftCcy))
					);
					lastRightAmount.current = fromPlainToDisplayValueWithRounding(
						newLeftAmt,
						leftCcySep,
						leftDecimalPoint,
						leftDecimalDigits,
						ROUND.HALF_UP
					);
					setLeftAmount(lastRightAmount.current);
				}
			}
		} else if (
			focusedSide === FOCUS_SIDE_RIGHT &&
			lastLeftCcy.current !== leftCcy
		) {
			if (leftCcy !== "" && rightCcy !== "") {
				lastLeftAmount.current = rightAmount;
				lastLeftCcy.current = leftCcy;
				lastRightCcy.current = rightCcy;
				const rightAmt = getNumber(
					rightAmount,
					rightCcySep,
					rightDecimalPoint,
					rightDecimalDigits
				);
				if (rightAmt > 0) {
					const newLeftAmt = Number(
						Big(rightAmt).times(calculateFxRate(rightCcy, leftCcy))
					);
					lastRightAmount.current = fromPlainToDisplayValueWithRounding(
						newLeftAmt,
						leftCcySep,
						leftDecimalPoint,
						leftDecimalDigits,
						ROUND.HALF_UP
					);
					setLeftAmount(lastRightAmount.current);
				}
			}
		} else if (focusedSide === "" && rightAmount === "" && leftAmount !== "") {
			if (leftCcy !== "" && rightCcy !== "") {
				const leftAmt = getNumber(
					leftAmount,
					leftCcySep,
					leftDecimalPoint,
					leftDecimalDigits
				);

				if (leftAmt > 0) {
					const newRightAmt = Number(
						Big(leftAmt).times(calculateFxRate(leftCcy, rightCcy))
					);
					lastRightAmount.current = fromPlainToDisplayValueWithRounding(
						newRightAmt,
						rightCcySep,
						rightDecimalPoint,
						rightDecimalDigits,
						ROUND.HALF_UP
					);
					setRightAmount(lastRightAmount.current);
				}
			}
		}

		const timer = setTimeout(() => {
			fireGA4Event();
		}, 5000);

		return () => clearTimeout(timer);
	}, [
		focusedSide,
		rightCcy,
		rightAmount,
		rightCcySep,
		rightDecimalPoint,
		rightDecimalDigits,
		calculateFxRate,
		leftCcy,
		leftCcySep,
		leftDecimalPoint,
		leftDecimalDigits,
		leftAmount,
	]);

	const fireGA4Event = () => {
		debugger;
	};

	return (
		<Grid container sx={boxStyle}>
			<Grid item direction="row" xs={12} md={5.5} lg={5.5} mt={1}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						pb: 1,
						pl: 0.5,
					}}
				>
					{focusedSide === "focus_left" ? (
						<LockIcon
							sx={{ color: WHITE_FFF, fontSize: 18, alignSelf: "center" }}
						/>
					) : (
						<></>
					)}
					<ThemeProvider theme={theme}>
						<Typography
							variant="body1"
							textAlign="left"
							pl={0.5}
							pt={0.2}
							color={WHITE_FFF}
						>
							From
						</Typography>
					</ThemeProvider>
				</Box>
				<CurrencySelectionView
					defaultValue={"SGD"}
					selectedValue={leftCcy}
					side={FOCUS_SIDE_LEFT}
					onChangeCcy={onChangeLeftCcy}
					displayAmount={leftAmount}
					onChangeValue={setLeftAmount}
					focusedSide={setFocusedSide}
				/>
				{leftCcy !== "" && rightCcy !== "" && (
					<ThemeProvider theme={theme}>
						<Box
							sx={{
								display: "flex",
								paddingTop: "10px",
							}}
						>
							<Typography variant="body1" textAlign="left">
								1 {leftCcyName}{" "}
							</Typography>
							<Typography textAlign="left" variant="body1">
								&nbsp;={" "}
							</Typography>
							&nbsp;
							<Typography
								variant="body1"
								textAlign="left"
								fontWeight="900"
								color={WHITE_FFF}
							>
								{leftCcyRate} {rightCcyName}
							</Typography>
						</Box>
					</ThemeProvider>
				)}
			</Grid>
			<Grid item direction="row" xs={12} md={5.5} lg={5.5} mt={1}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						pb: 1,
						pl: 0.5,
					}}
				>
					{focusedSide === "focus_right" ? (
						<LockIcon
							sx={{ color: WHITE_FFF, fontSize: 18, alignSelf: "center" }}
						/>
					) : (
						<></>
					)}
					<ThemeProvider theme={theme}>
						<Typography
							variant="body1"
							textAlign="left"
							pl={0.5}
							pt={0.2}
							color={WHITE_FFF}
						>
							To
						</Typography>
					</ThemeProvider>
				</Box>
				<CurrencySelectionView
					defaultValue={"USD"}
					selectedValue={rightCcy}
					side={FOCUS_SIDE_RIGHT}
					onChangeCcy={onChangeRightCcy}
					displayAmount={rightAmount}
					onChangeValue={setRightAmount}
					focusedSide={setFocusedSide}
				/>
				{leftCcy !== "" && rightCcy !== "" && (
					<ThemeProvider theme={theme}>
						<Box
							sx={{
								display: "flex",
								paddingTop: "10px",
							}}
						>
							<Typography variant="body1" textAlign="left">
								1 {rightCcyName}{" "}
							</Typography>
							<Typography textAlign="left" variant="body1">
								&nbsp;={" "}
							</Typography>
							&nbsp;
							<Typography
								variant="body1"
								textAlign="left"
								fontWeight="900"
								color={WHITE_FFF}
							>
								{rightCcyRate} {leftCcyName}
							</Typography>
						</Box>
					</ThemeProvider>
				)}
			</Grid>
		</Grid>
	);
}

export default ConversionView;
